import { AnimatePresence, motion, useInView } from "framer-motion"
import { StaticImage } from "gatsby-plugin-image"
import React, { useEffect, useRef, useState } from "react"
import { BlobCircleBackground } from "../BlobCircleBackground"

const testimonials = [
  {
    quote:
      "We switched to Premagic because our previous tools were too outdated to deliver the results we needed. Premagic is creative, modern, and incredibly user-friendly",
    name: "Shreya Shyamsunder",
    company: "Global Events Team",
    avatar: (
      <StaticImage
        src="../../images/testimonials/shreya.png"
        alt="Shreya Shyamsunder"
        placeholder="blurred"
        className="w-full h-full object-cover rounded-full"
        loading="lazy"
      />
    ),
    logo: (
      <StaticImage
        src="../../images/client-logos/dubai-world-trade-centre.png"
        alt="Global Events Team"
        className="w-full max-w-[120px] mx-auto"
      />
    ),
  },
  {
    quote:
      "It was a new idea that we implemented at Khaleej Times. And so far, everyone is so satisfied. The feedback from the audience and speakers was really amazing. ",
    name: "Mohammed Hegazy",
    company: "Khaleej Times",
    avatar: (
      <StaticImage
        src="../../images/testimonials/hegazy.png"
        alt="Mohammed Hegazy"
        placeholder="blurred"
        className="w-full h-full object-cover rounded-full"
        loading="lazy"
      />
    ),
    logo: (
      <StaticImage
        src="../../images/client-logos/latest/13.png"
        alt="Khaleej Times"
        className="w-full max-w-[120px] mx-auto"
      />
    ),
  },
  {
    quote:
      "Premagic doesn’t just capture moments; it creates a friction-free advocacy engine that turns every attendee into a promoter.",
    name: "Jay Weintraub",
    company: "CEO of Connective",
    avatar: (
      <StaticImage
        src="../../images/testimonials/jay-weintraub.png"
        alt="Jay Weintraub"
        placeholder="blurred"
        className="w-full h-full object-cover rounded-full"
        loading="lazy"
      />
    ),
    logo: (
      <StaticImage
        src="../../images/testimonials/concative.png"
        alt="Connective"
        className="w-full max-w-[50px] mx-auto"
      />
    ),
  },
  {
    quote:
      "The seamless photo distribution made it effortless for our community to share their experience, driving massive organic visibility for Big 5 Global.",
    name: "Saadia S",
    company: "Marketing Manager",
    avatar: (
      <StaticImage
        src="../../images/testimonials/saadia.png"
        alt="Saadia S"
        placeholder="blurred"
        className="w-full h-full object-cover rounded-full"
        loading="lazy"
      />
    ),
    logo: (
      <StaticImage
        src="../../images/client-logos/latest/dmg.svg"
        alt="Khaleej Times"
        className="w-full max-w-[120px] mx-auto"
      />
    ),
  },
  {
    quote:
      "Our partnership with Premagic has been a game-changer, turning standard event moments into a powerful, measurable engine for organic growth.",
    name: " Sandie McCoubrey",
    company: "Co-founder of MESA",
    avatar: (
      <StaticImage
        src="../../images/testimonials/sandie.png"
        alt="Sandie McCoubrey"
        placeholder="blurred"
        className="w-full h-full object-cover rounded-full"
        loading="lazy"
      />
    ),
    logo: (
      <StaticImage
        src="../../images/testimonials/mesa.png"
        alt="Khaleej Times"
        className="w-full max-w-[120px] mx-auto"
      />
    ),
  },
]

const AUTOPLAY_INTERVAL = 6000

export default function Testimonial({
  isDemoPage = false,
}: {
  isDemoPage?: boolean
}) {
  const [active, setActive] = useState(0)
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true })

  useEffect(() => {
    if (!isInView) return
    const timer = setInterval(() => {
      setActive(prev => (prev + 1) % testimonials.length)
    }, AUTOPLAY_INTERVAL)
    return () => clearInterval(timer)
  }, [isInView])

  if (isDemoPage) {
    return (
      <section className="overflow-hidden relative 2xl:pb-20 overflow-hidden border-t border-gray-100">
        <div
          ref={ref}
          className="relative flex items-center justify-center pt-10 px-8"
        >
          <div className="w-full text-center min-h-[450px] md:min-h-0  flex items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="flex flex-col items-center w-full"
              >
                <div className="flex items-center gap-4 mb-10 flex-col md:flex-row">
                  <div className="md:w-[250px] w-[80px] rounded-full overflow-hidden bg-gray-200">
                    {React.cloneElement(testimonials[active].avatar, {
                      className: "w-full h-full object-cover",
                    })}
                  </div>

                  <p className="text-md md:text-left text-center !leading-[1.4] font-medium  font-secondary text-primary-dark">
                    "{testimonials[active].quote}"
                  </p>
                </div>

                <div className="text-base mb-6">
                  <span className="font-semibold text-gray-900 mr-2">
                    {testimonials[active].name}
                  </span>
                  |
                  <span className="text-gray-400 ml-3">
                    {testimonials[active].company}
                  </span>
                </div>

                <div className="w-[100px]">{testimonials[active].logo}</div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="relative md:min-h-[75vh] 2xl:min-h-[60vh] 2xl:pb-20 min-h-[45vh] overflow-hidden bg-white">
      {/* bg blob animation */}
      <BlobCircleBackground />
      <div
        ref={ref}
        className="relative flex items-center justify-center md:pt-24 pt-10 px-8"
      >
        <div className="max-w-3xl w-full text-center min-h-[400px] flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="flex flex-col items-center w-full"
            >
              <div className="w-20 h-20 rounded-full mb-8 bg-gray-200">
                {React.cloneElement(testimonials[active].avatar, {
                  loading: active === 0 ? "eager" : "lazy",
                })}
              </div>

              <p className="text-md md:text-4xl !leading-[1.4] font-medium mb-10 font-secondary text-primary-dark">
                "{testimonials[active].quote}"
              </p>

              <div className="text-base mb-6">
                <span className="font-semibold text-gray-900 mr-2">
                  {testimonials[active].name}
                </span>
                |
                <span className="text-gray-400 ml-3">
                  {testimonials[active].company}
                </span>
              </div>

              <div>{testimonials[active].logo}</div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  )
}
